import { useState, useEffect, useRef } from "react";
import { X, RotateCcw, Send, Check, CheckCheck } from "lucide-react";
import { waLink } from "@/lib/config";
import api from "@/lib/api";

const WaIcon = ({ className }) => (
  <svg viewBox="0 0 32 32" className={className} fill="currentColor" aria-hidden="true">
    <path d="M16.004 3C9.383 3 4 8.383 4 15.004c0 2.11.553 4.168 1.605 5.98L4 29l8.21-1.57a11.93 11.93 0 0 0 3.79.62h.004C22.621 28.05 28 22.667 28 16.046 28 9.425 22.625 3 16.004 3zm0 21.86h-.003a9.86 9.86 0 0 1-3.74-.74l-.27-.11-4.87.93.93-4.75-.18-.29a9.84 9.84 0 0 1-1.51-5.25c0-5.45 4.44-9.88 9.91-9.88 2.65 0 5.13 1.03 7 2.9a9.8 9.8 0 0 1 2.9 6.99c0 5.45-4.44 9.99-9.9 9.99zm5.43-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.06 2.87 1.21 3.07.15.2 2.09 3.2 5.07 4.49.71.31 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.69.25-1.28.17-1.41-.07-.13-.27-.2-.57-.35z" />
  </svg>
);

const TypingDots = () => (
  <div className="flex items-center gap-1 px-1 py-0.5">
    <span className="typing-dot h-2 w-2 rounded-full bg-slate-400" style={{ animationDelay: "0ms" }} />
    <span className="typing-dot h-2 w-2 rounded-full bg-slate-400" style={{ animationDelay: "150ms" }} />
    <span className="typing-dot h-2 w-2 rounded-full bg-slate-400" style={{ animationDelay: "300ms" }} />
  </div>
);

export default function FloatingWhatsApp() {
  const [open, setOpen] = useState(false);
  const [teaser, setTeaser] = useState(true);
  const [messages, setMessages] = useState([]);
  const [options, setOptions] = useState([]);
  const [typing, setTyping] = useState(false);
  const [phase, setPhase] = useState(0);
  const [finalCta, setFinalCta] = useState(null);
  const [askPhone, setAskPhone] = useState(false);
  const [phone, setPhone] = useState("");
  const [choice, setChoice] = useState("");
  const [showCta, setShowCta] = useState(false);
  const [consent, setConsent] = useState(false);
  const bodyRef = useRef(null);
  const timers = useRef([]);

  const clearTimers = () => {
    timers.current.forEach((t) => clearTimeout(t));
    timers.current = [];
  };

  const wait = (ms) =>
    new Promise((res) => {
      const t = setTimeout(res, ms);
      timers.current.push(t);
    });

  const time = () =>
    new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const botSay = async (text, delay = 900) => {
    setTyping(true);
    setOptions([]);
    await wait(delay);
    setTyping(false);
    setMessages((m) => [...m, { from: "bot", text, t: time() }]);
    await wait(150);
  };

  const startFlow = async () => {
    await botSay("👋 Welcome to Digitify My Biz! I'm Ava, your automation assistant.", 700);
    await botSay(
      "We turn Meta Ads into booked appointments for real estate teams — automatically. What best describes you?",
      1100
    );
    setOptions(["I sell Services / B2B", "I'm a Real Estate Agent", "Just exploring"]);
  };

  const resetFlow = () => {
    clearTimers();
    setMessages([]);
    setOptions([]);
    setFinalCta(null);
    setTyping(false);
    setPhase(0);
    setAskPhone(false);
    setPhone("");
    setChoice("");
    setShowCta(false);
    setConsent(false);
  };

  useEffect(() => {
    if (open && messages.length === 0 && !typing) {
      startFlow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, typing, options, finalCta, askPhone, showCta]);

  useEffect(() => () => clearTimers(), []);

  const handleOption = async (opt) => {
    setMessages((m) => [...m, { from: "user", text: opt, t: time() }]);
    setOptions([]);

    if (phase === 0) {
      setPhase(1);
      await botSay("Perfect 🙌 Here's exactly how our automation works for you:", 900);
      await botSay(
        "The moment a lead clicks your Meta Ad, our bot replies in ~5 seconds, asks 3 smart questions to qualify them, then routes ONLY the hot leads to you — and books the viewing automatically. 🔥",
        1500
      );
      await botSay("How would you like to move forward?", 900);
      setOptions(["📅 Book a strategy call", "📄 Get our PDF proposal"]);
    } else if (phase === 1) {
      setPhase(2);
      const isCall = opt.toLowerCase().includes("call");
      setChoice(isCall ? "Strategy call" : "PDF proposal");
      setFinalCta({
        label: isCall ? "Book on WhatsApp" : "Get the proposal",
        text: isCall
          ? "Hi Digitify My Biz! I'd like to book a free strategy call for my real estate marketing."
          : "Hi Digitify My Biz! Please send me the real estate marketing proposal PDF.",
      });
      await botSay(
        isCall
          ? "Awesome! Drop your WhatsApp number below and we'll lock in your strategy call 👇"
          : "Great choice! Drop your WhatsApp number below and we'll send the proposal straight to your WhatsApp 👇",
        1200
      );
      setAskPhone(true);
    }
  };

  const submitPhone = async () => {
    if (!consent) return;
    const num = phone.trim();
    setMessages((m) => [...m, { from: "user", text: num || "(skipped)", t: time() }]);
    setAskPhone(false);
    try {
      await api.post("/whatsapp/notify", {
        name: "WhatsApp Widget Lead",
        phone: num,
        choice,
        source: "WhatsApp Widget Simulator",
      });
    } catch (e) {
      /* best-effort, never block the UX */
    }
    await botSay("All set! 🎉 Tap below to continue the conversation on WhatsApp.", 900);
    setShowCta(true);
  };

  return (
    <>
      {/* Chat window */}
      {open && (
        <div
          data-testid="whatsapp-chat-window"
          className="fixed z-[60] bottom-24 right-4 sm:right-6 w-[calc(100vw-2rem)] max-w-[380px] h-[540px] max-h-[78vh] rounded-2xl overflow-hidden shadow-2xl flex flex-col animate-fade-up"
          style={{ backgroundColor: "#0B141A" }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3" style={{ backgroundColor: "#1F2C34" }}>
            <div className="relative">
              <div className="h-10 w-10 rounded-full grid place-items-center" style={{ backgroundColor: "#25D366" }}>
                <WaIcon className="h-6 w-6 text-[#0B141A]" />
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-[#1F2C34]" style={{ backgroundColor: "#25D366" }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm leading-tight">Digitify My Biz · Ava</p>
              <p className="text-[#8696a0] text-xs">online · typically replies in 5s</p>
            </div>
            <button onClick={resetFlow} data-testid="whatsapp-restart" className="text-[#8696a0] hover:text-white p-1.5" aria-label="Restart">
              <RotateCcw className="h-4 w-4" />
            </button>
            <button onClick={() => setOpen(false)} data-testid="whatsapp-close" className="text-[#8696a0] hover:text-white p-1.5" aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div
            ref={bodyRef}
            className="flex-1 overflow-y-auto px-3.5 py-4 space-y-2"
            style={{
              backgroundColor: "#0B141A",
              backgroundImage:
                "radial-gradient(rgba(255,255,255,0.025) 1px, transparent 1px)",
              backgroundSize: "22px 22px",
            }}
          >
            <div className="flex justify-center mb-2">
              <span className="text-[10px] text-[#8696a0] bg-[#1f2c34] px-3 py-1 rounded-lg">
                🔒 Demo conversation · End-to-end automated
              </span>
            </div>

            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] rounded-lg px-3 py-2 text-sm leading-snug ${
                    m.from === "user" ? "text-white rounded-tr-none" : "text-slate-100 rounded-tl-none"
                  }`}
                  style={{ backgroundColor: m.from === "user" ? "#005C4B" : "#202C33" }}
                >
                  <p className="whitespace-pre-line">{m.text}</p>
                  <span className="flex items-center justify-end gap-1 mt-1 text-[10px] text-white/50">
                    {m.t}
                    {m.from === "user" && <CheckCheck className="h-3 w-3 text-[#53bdeb]" />}
                  </span>
                </div>
              </div>
            ))}

            {typing && (
              <div className="flex justify-start">
                <div className="rounded-lg rounded-tl-none px-2 py-2" style={{ backgroundColor: "#202C33" }}>
                  <TypingDots />
                </div>
              </div>
            )}

            {/* Quick reply options */}
            {options.length > 0 && (
              <div className="flex flex-col items-end gap-2 pt-2">
                {options.map((opt, i) => (
                  <button
                    key={opt}
                    data-testid={`whatsapp-option-${i}`}
                    onClick={() => handleOption(opt)}
                    className="text-[#53bdeb] font-medium text-sm bg-[#202C33] hover:bg-[#2a3942] border border-[#53bdeb]/30 rounded-lg px-4 py-2.5 transition-colors max-w-[85%] text-right"
                  >
                    {opt}
                  </button>
                ))}
              </div>
            )}

            {/* Phone capture */}
            {askPhone && (
              <div className="pt-2 space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    data-testid="whatsapp-phone-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && submitPhone()}
                    placeholder="+91 98765 43210"
                    className="flex-1 rounded-lg bg-[#202C33] text-white placeholder:text-[#8696a0] px-3 py-2.5 text-sm focus:outline-none"
                  />
                  <button
                    data-testid="whatsapp-phone-submit"
                    onClick={submitPhone}
                    disabled={!consent}
                    className="h-10 w-10 grid place-items-center rounded-full shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: "#25D366" }}
                    aria-label="Send number"
                  >
                    <Send className="h-4 w-4 text-[#0B141A]" />
                  </button>
                </div>
                <label className="flex items-start gap-2 cursor-pointer" data-testid="whatsapp-consent-label">
                  <input
                    type="checkbox"
                    data-testid="whatsapp-consent"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 cursor-pointer"
                    style={{ accentColor: "#25D366" }}
                  />
                  <span className="text-[10px] text-[#8696a0] leading-snug">
                    I agree to receive automated system updates and marketing case studies via WhatsApp from Digitify My Biz. Message &amp; data rates may apply.
                  </span>
                </label>
              </div>
            )}

            {/* Final CTA */}
            {showCta && finalCta && (
              <div className="pt-2">
                <a
                  href={waLink(finalCta.text)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="whatsapp-final-cta"
                  className="flex items-center justify-center gap-2 w-full rounded-lg px-4 py-3 font-semibold text-[#0B141A] transition-transform hover:scale-[1.02] active:scale-95"
                  style={{ backgroundColor: "#25D366" }}
                >
                  <WaIcon className="h-5 w-5" />
                  {finalCta.label}
                </a>
                <p className="text-center text-[10px] text-[#8696a0] mt-2">Opens a real WhatsApp chat with our team</p>
              </div>
            )}
          </div>

          {/* Decorative input bar */}
          <div className="flex items-center gap-2 px-3 py-2.5" style={{ backgroundColor: "#1F2C34" }}>
            <div className="flex-1 rounded-full px-4 py-2 text-sm text-[#8696a0]" style={{ backgroundColor: "#2a3942" }}>
              Pick an option above…
            </div>
            <div className="h-9 w-9 grid place-items-center rounded-full" style={{ backgroundColor: "#25D366" }}>
              <Send className="h-4 w-4 text-[#0B141A]" />
            </div>
          </div>
        </div>
      )}

      {/* Teaser badge */}
      {teaser && !open && (
        <div
          data-testid="whatsapp-teaser"
          className="fixed z-[55] bottom-[5.5rem] right-4 sm:right-6 w-[260px] glass-panel rounded-2xl rounded-br-sm p-3.5 animate-float cursor-pointer"
          onClick={() => setOpen(true)}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setTeaser(false);
            }}
            data-testid="whatsapp-teaser-close"
            className="absolute -top-2 -right-2 h-6 w-6 grid place-items-center rounded-full bg-brand-navy border border-white/10 text-slate-300 hover:text-white"
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <p className="text-sm text-slate-100 leading-snug">
            🔥 See how we turn Meta Ads into Sales <span className="text-brand-cyan font-semibold">instantly</span>. Click to test our bot!
          </p>
        </div>
      )}

      {/* Toggle button */}
      <button
        data-testid="whatsapp-widget-toggle"
        onClick={() => {
          setOpen((v) => !v);
          setTeaser(false);
        }}
        className={`fixed z-[60] bottom-5 right-4 sm:right-6 h-16 w-16 rounded-full grid place-items-center shadow-2xl transition-transform hover:scale-105 active:scale-95 ${
          open ? "" : "animate-pulse-ring"
        }`}
        style={{ backgroundColor: "#25D366" }}
        aria-label="Open WhatsApp chat simulator"
      >
        {open ? <X className="h-7 w-7 text-[#0B141A]" /> : <WaIcon className="h-8 w-8 text-[#0B141A]" />}
      </button>
    </>
  );
}
